import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, refreshAccessToken } from "@/api/client";

interface UseApiState<T> {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

/**
 * Fetcher hook with one ergonomic behaviour: on 401, attempt a
 * silent refresh + retry once. The retry is best-effort — if the
 * refresh fails, the original 401 propagates as ``error`` and the
 * UI should redirect to login.
 *
 * The hook intentionally does NOT cache or share results across
 * components. State libraries (TanStack Query / SWR) are forbidden
 * by the dependency policy; consumers that need cross-component
 * cache should hoist the fetch into a Context.
 */
export function useApi<T>(
  fetcher: () => Promise<T>,
  options: { enabled?: boolean; deps?: ReadonlyArray<unknown> } = {}
): UseApiState<T> {
  const { enabled = true, deps = [] } = options;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const cancelRef = useRef(false);

  const execute = useCallback(async (): Promise<void> => {
    cancelRef.current = false;
    setIsLoading(true);
    setError(null);
    try {
      let result: T;
      try {
        result = await fetcher();
      } catch (firstError) {
        // Retry once on 401 after a silent refresh.
        if (firstError instanceof ApiError && firstError.status === 401) {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            result = await fetcher();
          } else {
            throw firstError;
          }
        } else {
          throw firstError;
        }
      }
      if (!cancelRef.current) setData(result);
    } catch (e) {
      if (!cancelRef.current) {
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    } finally {
      if (!cancelRef.current) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher]);

  useEffect(() => {
    if (enabled) {
      void execute();
    }
    return () => {
      cancelRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return { data, error, isLoading, refetch: execute };
}

interface UseMutationState<TInput, TOutput> {
  mutate: (input: TInput) => Promise<TOutput | null>;
  isLoading: boolean;
  error: Error | null;
  reset: () => void;
}

/**
 * Mutation companion to :func:`useApi`. Same auto-401-refresh
 * semantics; same no-cache policy.
 */
export function useMutation<TInput, TOutput>(
  mutator: (input: TInput) => Promise<TOutput>,
  options: {
    onSuccess?: (data: TOutput) => void;
    onError?: (error: Error) => void;
  } = {}
): UseMutationState<TInput, TOutput> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: TInput): Promise<TOutput | null> => {
      setIsLoading(true);
      setError(null);
      try {
        let result: TOutput;
        try {
          result = await mutator(input);
        } catch (firstError) {
          if (firstError instanceof ApiError && firstError.status === 401) {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
              result = await mutator(input);
            } else {
              throw firstError;
            }
          } else {
            throw firstError;
          }
        }
        options.onSuccess?.(result);
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        options.onError?.(err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutator]
  );

  const reset = useCallback(() => {
    setError(null);
    setIsLoading(false);
  }, []);

  return { mutate, isLoading, error, reset };
}
